<script setup>
/* global axios */
// Histórico de execuções de um flow — full width, busca + filtros, linhas clicáveis.
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import KanbanSelect from './KanbanSelect.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const flowId = route.params.flowId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const flow = ref(null);
const executions = ref([]);
const loading = ref(true);

const search = ref('');
const statusFilter = ref('');

// Só a classe é estática; o rótulo é resolvido reativo (senão t() no setup cai no fallback en).
const STATUS_CLS = {
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  waiting: 'bg-amber-100 text-amber-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  waiting_response:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
  paused: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
};
const STATUS_KEY = {
  running: 'running',
  waiting: 'waiting',
  waiting_response: 'waiting',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  paused: 'paused',
};
const statusCls = s =>
  STATUS_CLS[s] || 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11';
const statusLabel = s =>
  STATUS_KEY[s] ? t(`CHATBOT.history.status.${STATUS_KEY[s]}`) : s;

const statusOptions = computed(() => [
  { value: '', label: t('CHATBOT.history.filters.allStatuses') },
  { value: 'running', label: t('CHATBOT.history.status.running') },
  { value: 'waiting', label: t('CHATBOT.history.status.waiting') },
  { value: 'completed', label: t('CHATBOT.history.status.completed') },
  { value: 'failed', label: t('CHATBOT.history.status.failed') },
  { value: 'paused', label: t('CHATBOT.history.status.paused') },
  { value: 'cancelled', label: t('CHATBOT.history.status.cancelled') },
]);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  return executions.value.filter(ex => {
    if (statusFilter.value) {
      // "waiting" agrupa waiting + waiting_response
      if (statusFilter.value === 'waiting') {
        if (!['waiting', 'waiting_response'].includes(ex.status)) return false;
      } else if (ex.status !== statusFilter.value) {
        return false;
      }
    }
    if (q) {
      const hay = `${ex.contact_name || ''} ${ex.contact_phone || ''} ${
        ex.contact_email || ''
      } ${ex.conversation_id || ''} #${ex.id}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
});

const load = async () => {
  loading.value = true;
  flow.value = (await axios.get(`${base}/flows/${flowId}`)).data.data;
  executions.value =
    (
      await axios.get(`${base}/flow_executions`, {
        params: { flow_id: flowId },
      })
    ).data.data || [];
  loading.value = false;
};
const cancel = async (ex, evt) => {
  evt?.stopPropagation();
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CHATBOT.history.cancelConfirm'))) return;
  await axios.delete(`${base}/flow_executions/${ex.id}`);
  await load();
};
const pause = async (ex, evt) => {
  evt?.stopPropagation();
  await axios.patch(`${base}/flow_executions/${ex.id}/pause`);
  await load();
};
const resume = async (ex, evt) => {
  evt?.stopPropagation();
  await axios.patch(`${base}/flow_executions/${ex.id}/resume`);
  await load();
};
const resetExec = async (ex, evt) => {
  evt?.stopPropagation();
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CHATBOT.history.resetConfirm'))) return;
  await axios.patch(`${base}/flow_executions/${ex.id}/reset`);
  await load();
};
const openDetail = ex =>
  router.push(
    `/app/accounts/${accountId}/flows/${flowId}/history/${ex.id}`
  );
const openConversation = (ex, evt) => {
  evt?.stopPropagation();
  if (ex.conversation_id)
    router.push(
      `/app/accounts/${accountId}/conversations/${ex.conversation_id}`
    );
};
const goBack = () => router.push(`/app/accounts/${accountId}/flows`);
const fmt = d => (d ? new Date(d).toLocaleString('pt-BR') : '—');
const isActive = s => ['running', 'waiting', 'waiting_response'].includes(s);

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <div class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div class="flex items-center h-16 gap-3 px-6">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="goBack"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 18l-6-6 6-6" />
          </svg>
          {{ t('CHATBOT.history.backTitle') }}
        </button>
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('CHATBOT.history.title') }} — {{ flow?.name }}
        </h1>
        <span
          v-if="flow"
          class="px-2 py-0.5 text-[0.625rem] rounded-full"
          :class="
            flow.flow_type === 'sequence'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
          "
          >{{
            flow.flow_type === 'sequence'
              ? t('CHATBOT.flowType.sequence')
              : t('CHATBOT.flowType.bot')
          }}</span
        >
        <button
          class="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="load"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 12a8 8 0 0 1 13.66-5.66L20 8M20 8V4M20 8h-4" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M20 12a8 8 0 0 1-13.66 5.66L4 16M4 16v4M4 16h4" />
          </svg>
          {{ t('CHATBOT.history.refresh') }}
        </button>
      </div>
    </div>

    <div class="w-full px-6 py-6">
      <!-- Busca + filtros -->
      <div class="flex flex-col sm:flex-row gap-3 mb-4">
        <div
          class="flex items-center gap-2 flex-1 h-9 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-1 transition-all duration-200 focus-within:outline-n-blue-9 hover:outline-n-slate-6"
        >
          <span class="i-lucide-search size-4 shrink-0 text-n-slate-10" />
          <input
            v-model="search"
            type="text"
            :placeholder="t('CHATBOT.history.filters.searchPlaceholder')"
            class="reset-base flex-1 min-w-0 h-full m-0 p-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
          />
        </div>
        <div class="w-full sm:w-52">
          <KanbanSelect v-model="statusFilter" :options="statusOptions" />
        </div>
      </div>

      <div v-if="loading" class="text-n-slate-11">
        {{ t('CHATBOT.history.loadingHistory') }}
      </div>
      <div
        v-else-if="!executions.length"
        class="py-12 text-center text-n-slate-10"
      >
        {{ t('CHATBOT.history.noExecutions') }}
      </div>
      <div
        v-else-if="!filtered.length"
        class="py-12 text-center text-n-slate-10"
      >
        {{ t('CHATBOT.history.filters.noMatch') }}
      </div>
      <table
        v-else
        class="w-full overflow-hidden text-sm bg-n-background border rounded-lg border-n-weak"
      >
        <thead class="text-left bg-n-alpha-1">
          <tr class="text-[0.6875rem] uppercase text-n-slate-10">
            <th class="px-4 py-3">{{ t('CHATBOT.history.table.contact') }}</th>
            <th class="px-4 py-3">{{ t('CHATBOT.history.table.status') }}</th>
            <th class="px-4 py-3">{{ t('CHATBOT.history.table.startTime') }}</th>
            <th class="px-4 py-3">{{ t('CHATBOT.history.table.completion') }}</th>
            <th class="px-4 py-3">{{ t('CHATBOT.history.table.error') }}</th>
            <th class="px-4 py-3 text-right">
              {{ t('CHATBOT.history.table.actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ex in filtered"
            :key="ex.id"
            class="border-t border-n-weak cursor-pointer hover:bg-n-alpha-1"
            @click="openDetail(ex)"
          >
            <td class="px-4 py-3 text-n-slate-12">
              {{ ex.contact_name || '—'
              }}<span
                v-if="ex.conversation_id"
                class="text-[0.6875rem] text-n-slate-10"
              >
                · #{{ ex.conversation_id }}</span
              >
            </td>
            <td class="px-4 py-3">
              <span
                class="px-2 py-0.5 text-[0.625rem] rounded-full"
                :class="statusCls(ex.status)"
                >{{ statusLabel(ex.status) }}</span
              >
            </td>
            <td class="px-4 py-3 text-n-slate-10">
              {{ fmt(ex.started_at || ex.created_at) }}
            </td>
            <td class="px-4 py-3 text-n-slate-10">{{ fmt(ex.completed_at) }}</td>
            <td
              class="px-4 py-3 text-n-ruby-11 text-[0.75rem] max-w-[180px] truncate"
            >
              {{ ex.error_message }}
            </td>
            <td class="px-4 py-3 text-right whitespace-nowrap">
              <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                :title="t('CHATBOT.history.detail.open')"
                @click.stop="openDetail(ex)"
              >
                <span class="i-lucide-eye w-4 h-4" />
              </button>
              <button
                v-if="ex.conversation_id"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                :title="t('CHATBOT.history.openConversation')"
                @click.stop="openConversation(ex, $event)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 21 11.5z" />
                </svg>
              </button>
              <button
                v-if="isActive(ex.status)"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                :title="t('SEQUENCES.executions.pause')"
                @click.stop="pause(ex, $event)"
              >
                <span class="i-lucide-pause w-4 h-4" />
              </button>
              <button
                v-if="ex.status === 'paused'"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-blue-11 hover:bg-n-alpha-2"
                :title="t('SEQUENCES.executions.resume')"
                @click.stop="resume(ex, $event)"
              >
                <span class="i-lucide-play w-4 h-4" />
              </button>
              <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-amber-11 hover:bg-n-alpha-2"
                :title="t('CHATBOT.history.reset')"
                @click.stop="resetExec(ex, $event)"
              >
                <span class="i-lucide-rotate-ccw w-4 h-4" />
              </button>
              <button
                v-if="isActive(ex.status) || ex.status === 'paused'"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-ruby-11 hover:bg-n-alpha-2"
                :title="t('CHATBOT.history.actions.cancel')"
                @click.stop="cancel(ex, $event)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
                  <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
